/**
 * AlignmentWarnings — CCA-M1
 *
 * Renders conflict chips for alignment warnings found during decision creation.
 * Each chip shows the principle title, reason, and an Acknowledge button.
 *
 * Renders nothing when `conflicts` is empty or status is 'skipped'.
 */

import React from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import type { AlignmentConflict } from '@/services/roros/alignment.service';

interface AlignmentWarningsProps {
  conflicts: AlignmentConflict[];
  /** Called when the user acknowledges a conflict. Receives principleId. */
  onAcknowledge?: (principleId: string) => void;
  /** Set of principleIds that have already been acknowledged in this session */
  acknowledgedIds?: Set<string>;
  /** True when an acknowledge mutation is in flight */
  isAcknowledging?: boolean;
  /**
   * [2] When false, the Acknowledge button is disabled and shows a save-first tooltip.
   * Defaults to true. Set to false before the decision has been saved (pre-save state).
   */
  acknowledgeEnabled?: boolean;
  'data-testid'?: string;
}

// [8] Keep mapping as plain English keys; call t() at render time (not module level)
// so translations resolve correctly in the active locale.
const LAYER_LABEL_KEYS: Record<string, string> = {
  governance: 'Governance',
  'company-principles': 'Company Principles',
  'core-values': 'Core Values',
  'mission-vision': 'Mission & Vision',
};

export function AlignmentWarnings({
  conflicts,
  onAcknowledge,
  acknowledgedIds = new Set(),
  isAcknowledging = false,
  acknowledgeEnabled = true,
  'data-testid': testId,
}: AlignmentWarningsProps) {
  if (!conflicts || conflicts.length === 0) {
    return null;
  }

  return (
    <div
      className="space-y-2"
      data-testid={testId ?? 'alignment-warnings'}
      role="region"
      aria-label={t('Alignment Warnings')}
    >
      <h3 className="text-sm font-semibold text-warning-700 flex items-center gap-1.5">
        <AlertTriangle className="h-4 w-4" aria-hidden />
        {t('Alignment Conflicts Detected')}
      </h3>
      <p className="text-xs text-muted-foreground">
        {t('This decision may conflict with your company context. Review each item and acknowledge to proceed.')}
      </p>

      <div className="space-y-2">
        {conflicts.map((conflict) => {
          const isAcknowledged = acknowledgedIds.has(conflict.principleId);
          // [8] Translate layer label at render time
          const layerLabel = t(LAYER_LABEL_KEYS[conflict.layer] ?? conflict.layer);

          return (
            <div
              key={conflict.principleId}
              className={
                'rounded-md border p-3 text-sm ' +
                (isAcknowledged
                  ? 'border-success-200 bg-success-50'
                  : 'border-warning-200 bg-warning-50')
              }
              data-testid={`alignment-conflict-${conflict.principleId}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="inline-flex items-center rounded-full bg-warning-100 px-2 py-0.5 text-xs font-medium text-warning-800 border border-warning-200">
                      {layerLabel}
                    </span>
                    <span className="font-medium text-foreground truncate">
                      {conflict.principleTitle}
                    </span>
                  </div>
                  <p className="mt-1 text-muted-foreground text-xs">
                    {conflict.reason}
                  </p>
                </div>

                {isAcknowledged ? (
                  <div className="flex items-center gap-1 text-success-600 flex-shrink-0 text-xs font-medium">
                    <CheckCircle className="h-4 w-4" aria-hidden />
                    {t('Acknowledged')}
                  </div>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="flex-shrink-0 text-xs"
                    disabled={isAcknowledging || !acknowledgeEnabled}
                    title={!acknowledgeEnabled ? t('Save the decision first to acknowledge') : undefined}
                    onClick={() => onAcknowledge?.(conflict.principleId)}
                    data-testid={`acknowledge-btn-${conflict.principleId}`}
                    aria-label={`${t('Acknowledge')} ${conflict.principleTitle}`}
                  >
                    {t('Acknowledge')}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
